export interface UserFeedback {
  id: string;
  timestamp: number;
  type: 'bug_report' | 'feature_request' | 'general_feedback' | 'usability_issue' | 'performance_issue';
  title: string;
  description: string;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  url: string;
  context?: {
    feature?: string;
    action?: string;
    error?: string;
    browserInfo?: { name: string; version: string; os: string };
  };
  status: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
}

export interface FeedbackConfig {
  enabled: boolean;
  maxFeedbacks: number;
  categories: string[];
}

const DEFAULT_CONFIG: FeedbackConfig = {
  enabled: true,
  maxFeedbacks: 50,
  categories: ['general', 'ui_ux', 'detection_accuracy', 'export', 'performance', 'accessibility'],
};

export class UserFeedbackManager {
  private static instance: UserFeedbackManager;
  private feedbacks: UserFeedback[] = [];
  private config: FeedbackConfig;
  private storageKey = 'allergen-lens:user-feedbacks';

  static getInstance(config?: Partial<FeedbackConfig>): UserFeedbackManager {
    if (!UserFeedbackManager.instance) {
      UserFeedbackManager.instance = new UserFeedbackManager(config);
    }
    return UserFeedbackManager.instance;
  }

  private constructor(config?: Partial<FeedbackConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.loadFeedbacks();
  }

  submitFeedback(feedback: Omit<UserFeedback, 'id' | 'timestamp' | 'status'>): string {
    if (!this.config.enabled) throw new Error('Feedback system is disabled');

    const entry: UserFeedback = {
      id: `feedback_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      timestamp: Date.now(),
      status: 'pending',
      ...feedback,
      url: feedback.url || window.location.href,
      context: { ...feedback.context, browserInfo: this.getBrowserInfo() },
    };

    this.feedbacks.push(entry);
    if (this.feedbacks.length > this.config.maxFeedbacks) {
      this.feedbacks = this.feedbacks.slice(-this.config.maxFeedbacks);
    }
    this.saveFeedbacks();
    return entry.id;
  }

  getFeedbacks(): UserFeedback[] { return [...this.feedbacks]; }
  exportFeedbacks(): string {
    return JSON.stringify({ feedbacks: this.feedbacks, exportedAt: new Date().toISOString() }, null, 2);
  }

  private getBrowserInfo(): { name: string; version: string; os: string } {
    const ua = navigator.userAgent;
    const browser =
      ua.includes('Chrome') ? { name: 'Chrome', rx: /Chrome\/(\d+)/ } :
      ua.includes('Firefox') ? { name: 'Firefox', rx: /Firefox\/(\d+)/ } :
      ua.includes('Safari') ? { name: 'Safari', rx: /Version\/(\d+)/ } :
      ua.includes('Edge') ? { name: 'Edge', rx: /Edge\/(\d+)/ } :
      { name: 'Unknown', rx: null };
    const version = browser.rx ? (ua.match(browser.rx)?.[1] ?? 'Unknown') : 'Unknown';
    const os =
      ua.includes('Windows') ? 'Windows' :
      ua.includes('Mac') ? 'macOS' :
      ua.includes('Linux') ? 'Linux' :
      ua.includes('Android') ? 'Android' :
      ua.includes('iOS') ? 'iOS' : 'Unknown';
    return { name: browser.name, version, os };
  }

  private loadFeedbacks(): void {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (raw) this.feedbacks = JSON.parse(raw);
    } catch { /* ignore */ }
  }

  private saveFeedbacks(): void {
    try { localStorage.setItem(this.storageKey, JSON.stringify(this.feedbacks)); }
    catch { /* ignore */ }
  }
}

export const userFeedbackManager = UserFeedbackManager.getInstance();

export function buildMailtoUrl(feedback: Omit<UserFeedback, 'id' | 'timestamp' | 'status'>): string | null {
  const recipient = import.meta.env.VITE_FEEDBACK_EMAIL as string | undefined;
  if (!recipient) return null;

  const typeLabel: Record<UserFeedback['type'], string> = {
    bug_report: 'Bug Report',
    feature_request: 'Feature Request',
    general_feedback: 'General Feedback',
    usability_issue: 'Usability Issue',
    performance_issue: 'Performance Issue',
  };

  const subject = `[Allergen Lens ${typeLabel[feedback.type]}] ${feedback.title}`;

  const body = [
    `Type: ${typeLabel[feedback.type]}`,
    `Category: ${feedback.category}`,
    `Priority: ${feedback.priority}`,
    ``,
    `Description:`,
    feedback.description,
    ``,
    `---`,
    `URL: ${feedback.url}`,
    `User Agent: ${navigator.userAgent}`,
  ].join('\n');

  return `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
